import React from 'react';
import { useMutation } from '@apollo/client';
import EditInfoModal from '../../../../components/EditInfoModal';
import {
  IDashboardDuplicateAnalysisMutation,
  IDashboardDuplicateAnalysisMutationVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { DASHBOARD_DUPLICATE_ANALYSIS } from '../../../../utils/apollo/dashboards';
import { assertNotNil } from '../../../../utils/types';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import {
  closeModal,
  IDashboardDuplicateReportData,
} from '../../../../redux/slices/modal';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectModalData } from '../../../../redux/selectors/modal';
import useUpdateDashboardStore from '../../utils/useUpdateDashboardStore';
import useDashboardSelector from '../../../../redux/selectorHooks/useDashboardSelector';
import toastMessage from '../../../../utils/toastMessage';
import { useAnalytics } from '../../../../utils/analytics/useAnalytics';
import { dashboardUpdated } from '../../../../utils/analytics/events/dashboard/events';

const DashboardDuplicateReportModal = () => {
  const dispatch = useAppDispatch();
  const updateDashboardStore = useUpdateDashboardStore();
  const { track } = useAnalytics();

  const dashboard = useDashboardSelector('dashboard');
  assertNotNil(dashboard);
  const { display, description, analysisId, pref } = useAppSelector(
    selectModalData,
  ) as IDashboardDuplicateReportData;

  const [executeDuplicateAnalysis, { loading: isDuplicateAnalysisLoading }] =
    useMutation<
      IDashboardDuplicateAnalysisMutation,
      IDashboardDuplicateAnalysisMutationVariables
    >(DASHBOARD_DUPLICATE_ANALYSIS);

  const handleClose = () => {
    dispatch(closeModal());
  };

  const handleSubmit = async (newName: string, newDescription: string) => {
    const { data } = await executeDuplicateAnalysis({
      variables: {
        input: {
          baseId: dashboard.baseId,
          instanceId: dashboard.id,
          analysis: {
            analysisId,
            name: newName,
            description: newDescription,
            pref,
          },
        },
      },
    });

    if (printGraphqlErrors(data?.dashboardDuplicateAnalysis?.errors)) {
      return;
    }

    const updatedDashboard = data?.dashboardDuplicateAnalysis?.entity;
    assertNotNil(updatedDashboard);

    track(dashboardUpdated(updatedDashboard));
    updateDashboardStore({ dashboard: updatedDashboard });

    toastMessage({
      message: 'Report has been successfully duplicated',
      type: 'info',
    });

    handleClose();
  };

  return (
    <EditInfoModal
      entity="duplicateDashboardReport"
      name={`${display} 1`}
      description={description}
      onClose={handleClose}
      onSubmit={handleSubmit}
      entitiesList={[]}
      isLoading={isDuplicateAnalysisLoading}
      submitLabel={'Duplicate'}
      trackContext="dashboards"
      trackSubmitAction="duplicate report - duplicate"
      trackCancelAction="duplicate report - cancel"
    />
  );
};

export default DashboardDuplicateReportModal;
